//= require personalised-config.js
//= require personalised-utils.js
// CUSTOM: personalised-bundle-cart-sync.js

(function() {
  // Ensure namespace exists
  window.personalised = window.personalised || {};
  
  // Set up a global sync state object to track sync status across the application
  window.personalised.cartSyncState = window.personalised.cartSyncState || {
    syncInProgress: false
  };
  
  // Operation queue system to prevent race conditions
  const operationQueue = {
    queue: [],
    processing: false,
    operationTimeout: window.personalised.config.timing.operationTimeout, // Timeout for any cart operation
    
    // Add an operation to the queue
    enqueue: function(operation) {
      return new Promise((resolve, reject) => {
        this.queue.push({
          operation,
          resolve,
          reject
        });
        
        // Start processing if not already in progress
        if (!this.processing) {
          this.processNext();
        }
      });
    },
    
    // Process the next operation in the queue
    processNext: function() {
      if (this.queue.length === 0) {
        this.processing = false;
        return;
      }
      
      this.processing = true;
      const { operation, resolve, reject } = this.queue.shift();
      
      // Create a timeout guard to prevent hanging operations
      let operationCompleted = false;
      const timeoutId = setTimeout(() => {
        if (!operationCompleted) {
          console.error('Cart operation timed out after ' + this.operationTimeout + 'ms');
          reject(new Error('Cart operation timed out after ' + this.operationTimeout + 'ms'));
          this.processNext(); // Continue to next operation after timeout
        }
      }, this.operationTimeout);
      
      // Execute the operation with timeout protection
      Promise.resolve()
        .then(() => operation())
        .then(result => {
          operationCompleted = true;
          clearTimeout(timeoutId);
          resolve(result);
          this.processNext(); // Continue to the next operation
        })
        .catch(error => {
          operationCompleted = true;
          clearTimeout(timeoutId);
          reject(error);
          this.processNext(); // Continue even after error
        });
    }
  };
    
  // Cart sync module
  const cartSync = {
    // Flag to track sync status
    _syncInProgress: false,
    _debounceTimer: null,
    
    // Retry configuration
    _maxRetries: 3,
    _retryDelay: 1000, // Base delay in ms before exponential backoff
    
    // Helper function to debounce callbacks
    _debounceWait: function(callback, delay = 500) {
      const self = this;
      clearTimeout(self._debounceTimer);
      self._debounceTimer = setTimeout(callback, delay);
    },
    
    // Helper function to create a timeout promise
    _timeout: function(ms) {
      return new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Cart sync operation timed out after ' + ms + 'ms')), ms)
      );
    },
    
    // Retry fetch with exponential backoff
    _retryFetch: async function(url, options = {}, retries = this._maxRetries) {
      try {
        const response = await fetch(url, options);
        
        // Check if the response is ok (status in the range 200-299)
        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(`HTTP error ${response.status}: ${errorData}`);
        }
        
        return response;
      } catch (error) {
        if (retries === 0) {
          console.error(`Failed fetch to ${url} after multiple retries:`, error);
          throw error;
        }
        
        // Calculate backoff delay with some randomness to prevent synchronized retries
        const delay = this._retryDelay * Math.pow(2, this._maxRetries - retries) + (Math.random() * 200);
        console.warn(`Fetch to ${url} failed. Retrying in ${Math.round(delay)}ms... (${retries} retries left)`, error);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Retry with one fewer retry attempt
        return this._retryFetch(url, options, retries - 1);
      }
    },
    
    // Core cart rebuild function - this will now be wrapped by the queue
    _performRebuildInCart: async function(lineItems) {
      // Set sync flag - now managed by the queue system
      window.personalised.cartSyncState.syncInProgress = true;
      
      try {
        // Define the cart sync operation as an async function
        // Get current cart with retry
        let cart;
        try {
          const res = await this._retryFetch(window.personalised.config.api.cart);
          cart = await res.json();
        } catch (error) {
          console.error('Failed to fetch cart after multiple retries:', error);
          throw new Error('Failed to load cart data. Please refresh the page and try again.');
        }
        
        // Find ALL personalised-bundle line items
        const bundleParentKey = window.personalised.config.bundle.BUNDLE_PARENT_PROPERTY_KEY;
        // Use centralized config for Recharge property keys
        const BUNDLE_PARENT_PROPERTY = window.personalised.config.bundle.RECHARGE_KEYS.BUNDLE_PARENT_PROPERTY;
        const bundleItems = cart.items.filter(
          item =>
            item.properties &&
            item.properties[BUNDLE_PARENT_PROPERTY] === bundleParentKey
        );

        // Batch all removals into a single /cart/update.js request
        if (bundleItems.length > 0) {
          const updates = {};
          for (const bundle of bundleItems) {
            updates[bundle.key] = 0;
          }
          
          try {
            const frequency = window.personalised.bundle.getFrequency();
            const hasNonPersonalised = cart.items.some(item =>
              !item.properties || item.properties['_rc_bundle_parent'] !== 'personalised-pack'
            );
            await this._retryFetch(window.personalised.config.api.cartUpdate, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                updates,
                attributes: {
                  frequency,
                  hasNonPersonalised: hasNonPersonalised ? 'true' : 'false'
                }
              })
            });
          } catch (error) {
            console.error('Failed to remove bundle items from cart after multiple retries:', error);
            throw new Error('Failed to update your bundle. Please try again.');
          }
        }

        // Add new items in a single request
        if (lineItems && lineItems.length > 0) {
          try {
            await this._retryFetch(window.personalised.config.api.cartAdd, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: lineItems })
            });
          } catch (error) {
            console.error('Failed to add bundle items to cart after multiple retries:', error);
            throw new Error('Failed to add products to your bundle. Please try again.');
          }
        }
          
        // Notify of successful sync
        if (window.personalised && window.personalised.eventBus) {
          window.personalised.eventBus.emit('syncSucceeded', { 
            message: 'Your personalized bundle has been updated' 
          });
        }

        // Refactor: publish cart update with required section HTML fragments to theme's pub-sub system
        async function getCartSectionIds() {
          const cartDrawers = document.querySelectorAll('[id^="CartDrawer"][data-section-id]');
          const sectionIds = [];
          cartDrawers.forEach(drawer => {
            // Extract only the raw section ID
            const sectionId = drawer.getAttribute('data-section-id');
            if (sectionId) {
              sectionIds.push(`sections--${sectionId}__cart-drawer`);
              sectionIds.push(`sections--${sectionId}__cart-items`);
            }
          });
          return sectionIds;
        }

        async function fetchCartSections(sectionKeys) {
          const url = `/?sections=${encodeURIComponent(sectionKeys.join(','))}`;
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Failed to fetch cart sections');
          return await response.json();
        }

        async function fetchCartJson() {
          const response = await fetch('/cart.js', { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Failed to fetch cart JSON');
          return await response.json();
        }

        async function publishCartUpdateToThemePubSub() {
          try {
            const sectionKeys = await getCartSectionIds();
            if (sectionKeys.length === 0) {
              // Fallback: just update cart count if no section IDs found
              const cartJson = await fetchCartJson();
              if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.publish) {
                window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
              }
              return;
            }

            // Fetch cart JSON first, then log its contents before section fetch
            const cartJson = await fetchCartJson();

            // Now fetch section HTML
            const sections = await fetchCartSections(sectionKeys);

            // Attach sections to cartJson
            cartJson.sections = sections;

            // Publish to theme pub-sub
            if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.publish) {
              // Add skipFocusHandling flag to prevent focus errors when cart drawer isn't shown
              window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { 
                cart: cartJson,
                skipFocusHandling: true 
              });
            }
          } catch (err) {
            if (window.personalised && window.personalised.debugMode) {
              console.warn('[Personalised Bundle] Failed to publish cart update with sections:', err);
            }
          }
        }

        await publishCartUpdateToThemePubSub();
        
        return true;
      } catch (err) {
        // Log errors but don't break the UI
        console.error('❌ Error in bundle cart sync:', err);
        return false;
      } finally {
        window.personalised.cartSyncState.syncInProgress = false;
      }
    },
    
    // Wrapped rebuild function that includes one retry
    rebuildInCart: async function(lineItems) {
      try {
        const result = await this._performRebuildInCart(lineItems);
        if (result === false) {
          console.log('Cart sync failed, retrying once in 1 second...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          return await this._performRebuildInCart(lineItems);
        }
        return result;
      } catch (err) {
        console.error('Cart sync failed after retry:', err);
        return false;
      }
    },
    
    // Updated debounced version - now also uses the queue system
    debouncedRebuildInCart: function(lineItems, message = null) {
      const self = this;
        // Use our internal debounce function 
      self._debounceWait(async function() {
        // Mark that sync is starting and update UI accordingly
        const utils = window.personalised.utils;
        
        // Set internal module flag
        self._syncInProgress = true;
        
        // Use utils only if available
        if (utils && typeof utils.startSync === 'function') {
          utils.startSync();
        }
        
        // Notify that sync is starting (event consumers can update UI)
        if (window.personalised && window.personalised.eventBus) {
          window.personalised.eventBus.emit('syncStarted', { message: message });
        }
        
        try {
          // Use the queue system for the actual operation
          await operationQueue.enqueue(() => self._performRebuildInCart(lineItems));
          
          // Notify that sync is complete successfully
          if (window.personalised && window.personalised.eventBus) {
            window.personalised.eventBus.emit('syncCompleted', { message });
          }
        } catch (err) {
          // Handle the error but don't throw it further
          if (window.personalised && window.personalised.eventBus) {
            let errorMessage = 'Failed to update your personalized bundle in cart';
            
            if (err.message) {
              errorMessage = err.message;
            }
            
            window.personalised.eventBus.emit('syncFailed', { 
              error: err, 
              message: errorMessage
            });
          }
          
          console.error('Error in debounced cart sync:', err);
        } finally {
          // Reset state regardless of outcome
          self._syncInProgress = false;
          
          // Reset utils flag if available
          if (utils && typeof utils.completeSync === 'function') {
            utils.completeSync();
          }
        }
      }, 10);
      
      // Return lineItems for chaining
      return lineItems;
    }
  };
  
  // Assign to single namespace
  window.personalised.cartSync = cartSync;
})();

// NOTE: All event handling for [data-add-to-pack] is now delegated in personalised-bundle-manager.js
// Use data-* attributes for any future selectors or event logic
